import { ScriptConverter, type ScriptOutputOption } from '../base.js';
import type {
  ContextualBlock,
  InteractiveScriptOptions,
  InteractiveScriptResult,
  ScriptLanguage,
} from '../../script-generator/types.js';
import { simpleHash } from './bash.js';

const POWERSHELL_LANGUAGES = new Set([
  'powershell',
  'pwsh',
  'ps1',
]);

export function isPowerShellLanguage(language: string | null): boolean {
  if (!language) return false;
  return POWERSHELL_LANGUAGES.has(language.toLowerCase().trim());
}

/**
 * The main interactive script template (PowerShell)
 */
export const INTERACTIVE_TEMPLATE = `#Requires -Version 5.1

<#
.SYNOPSIS
    {{TITLE}}

.DESCRIPTION
    Interactive installation script generated by Leedoc.
    Runs {{STEP_COUNT}} step(s) with per-step confirmation, progress tracking,
    and resume capability. Safe to re-run — completed steps are skipped
    based on a checkpoint file.

    Generated: {{TIMESTAMP}}

.PARAMETER Auto
    Run all steps without prompts. Exits on first failure.

.PARAMETER Resume
    Resume from the last saved checkpoint (same as re-running without -Restart).

.PARAMETER Restart
    Clear the checkpoint and start from step 1.

.PARAMETER Help
    Show this help text.

.EXAMPLE
    .\\script.ps1
    Run interactively, prompting before each step.

.EXAMPLE
    .\\script.ps1 -Auto
    Run all steps without prompts.

.EXAMPLE
    .\\script.ps1 -Restart
    Discard previous progress and start from the beginning.

.NOTES
    Controls during execution:
      Enter   Execute the step
      s       Skip this step
      q       Quit (progress saved)
#>

[CmdletBinding()]
param(
  [switch]$Auto,
  [switch]$Resume,
  [switch]$Restart,
  [switch]$Help
)

$ErrorActionPreference = 'Stop'
if ($PSVersionTable.PSVersion.Major -ge 7) {
  $PSNativeCommandUseErrorActionPreference = $true
}

# Configuration
$script:Title = '{{TITLE_ESCAPED}}'
$script:TotalSteps = {{STEP_COUNT}}
$script:CheckpointFile = if ($env:LEEDOC_CHECKPOINT) { $env:LEEDOC_CHECKPOINT } else { {{CHECKPOINT_FILE_EXPRESSION}} }
$script:AllowResume = \${{ALLOW_RESUME}}
$script:AutoMode = [bool]$Auto
$script:CurrentStep = 0

if ($Help) {
  Get-Help -Full $PSCommandPath
  exit 0
}

if ($Restart -and (Test-Path $script:CheckpointFile)) {
  Remove-Item $script:CheckpointFile -Force
}

if ($Resume) { $script:AllowResume = $true }

$script:UseColor = -not [Console]::IsOutputRedirected

function Write-Color {
  param(
    [Parameter(Mandatory, Position=0)][AllowEmptyString()][string]$Message,
    [ConsoleColor]$Color = [ConsoleColor]::Gray,
    [switch]$NoNewline
  )
  if ($script:UseColor) {
    Write-Host $Message -ForegroundColor $Color -NoNewline:$NoNewline
  } else {
    Write-Host $Message -NoNewline:$NoNewline
  }
}

#
# Checkpoint management
#

function Get-Checkpoint {
  if (-not $script:AllowResume) { return }
  if (-not (Test-Path $script:CheckpointFile)) { return }
  $raw = Get-Content $script:CheckpointFile -ErrorAction SilentlyContinue | Select-Object -First 1
  $saved = 0
  [int]::TryParse($raw, [ref]$saved) | Out-Null
  if ($saved -gt 0 -and $saved -lt $script:TotalSteps) {
    Write-Host ''
    Write-Color "Found checkpoint at step $saved/$($script:TotalSteps)" -Color Yellow
    if (-not $script:AutoMode) {
      Write-Host "Resume from step $($saved + 1)? [Y/n/restart] " -NoNewline
      $response = Read-Host
      switch -Regex ($response) {
        '^[nN]$' { $script:CurrentStep = 0 }
        '^(restart|[rR])$' {
          $script:CurrentStep = 0
          Remove-Item $script:CheckpointFile -Force
        }
        default { $script:CurrentStep = $saved }
      }
    } else {
      $script:CurrentStep = $saved
    }
  }
}

function Save-Checkpoint {
  param([int]$StepNum)
  $script:CurrentStep = $StepNum
  Set-Content -Path $script:CheckpointFile -Value $StepNum -NoNewline
}

function Clear-Checkpoint {
  if (Test-Path $script:CheckpointFile) {
    Remove-Item $script:CheckpointFile -Force
  }
}

#
# Display functions
#

function Write-StepHeader {
  param([int]$StepNum, [string]$Heading)
  Write-Host ''
  Write-Color '════════════════════════════════════════════════════════════════' -Color Blue
  Write-Color "Step $StepNum/$($script:TotalSteps): $Heading" -Color Cyan
  Write-Color '════════════════════════════════════════════════════════════════' -Color Blue
}

function Write-Description {
  param([string]$Desc)
  if ($Desc) {
    Write-Host ''
    Write-Host $Desc
  }
}

function Write-CommandPreview {
  param([string]$Preview)
  Write-Host ''
  Write-Color 'Commands to execute:' -Color Yellow
  Write-Color '────────────────────────────────────────' -Color DarkGray
  Write-Host $Preview
  Write-Color '────────────────────────────────────────' -Color DarkGray
}

#
# User interaction
#

function Confirm-Step {
  if ($script:AutoMode) { return $true }
  Write-Host ''
  Write-Color "Press Enter to execute, 's' to skip, 'q' to quit: " -Color Green -NoNewline
  $response = Read-Host
  switch -Regex ($response) {
    '^[sS]$' { return $false }
    '^[qQ]$' {
      Write-Host ''
      Write-Color "Aborted. Progress saved at step $($script:CurrentStep)." -Color Yellow
      exit 0
    }
    default { return $true }
  }
}

function Invoke-FailurePrompt {
  param([int]$StepNum)
  if ($script:AutoMode) {
    Write-Host ''
    Write-Color "Step $StepNum failed. Aborting (auto mode)." -Color Red
    exit 1
  }
  Write-Host ''
  Write-Color "Step $StepNum failed." -Color Red
  Write-Color 'Continue anyway? [y/N] ' -Color Yellow -NoNewline
  $response = Read-Host
  if ($response -notmatch '^[yY]$') { exit 1 }
}

{{STEP_FUNCTIONS}}

#
# Main execution
#

function Invoke-Main {
  Write-Host ''
  Write-Color $script:Title -Color Cyan
  Write-Color "Total steps: $($script:TotalSteps)" -Color DarkGray
  Write-Host ''
  Get-Checkpoint

{{STEP_CALLS}}

  Clear-Checkpoint
  Write-Host ''
  Write-Color '✓ All steps completed successfully!' -Color Green
  Write-Host ''
}

Invoke-Main
`;

/**
 * Template for a single step function
 */
export const STEP_FUNCTION_TEMPLATE = `
# Step {{STEP_NUM}}: {{HEADING_COMMENT}}
function Get-Step{{STEP_NUM}}Description {
  return ({{DESCRIPTION_LINES}}) -join "\`n"
}

function Get-Step{{STEP_NUM}}Preview {
  return ({{PREVIEW_LINES}}) -join "\`n"
}

function Invoke-Step{{STEP_NUM}} {
{{COMMAND_BODY}}
}
`;

/**
 * Template for step execution call
 */
export const STEP_CALL_TEMPLATE = `  # Step {{STEP_NUM}}
  if ({{STEP_NUM}} -le $script:CurrentStep) {
    Write-Color "✓ Step {{STEP_NUM}} already completed, skipping..." -Color Green
  } else {
    Write-StepHeader {{STEP_NUM}} '{{HEADING_ESCAPED}}'
    Write-Description (Get-Step{{STEP_NUM}}Description)
    Write-CommandPreview (Get-Step{{STEP_NUM}}Preview)
    if (Confirm-Step) {
      Write-Host ''
      Write-Color 'Executing...' -Color Cyan
      Write-Host ''
      try {
        Invoke-Step{{STEP_NUM}}
        Write-Host ''
        Write-Color "✓ Step {{STEP_NUM}} completed successfully" -Color Green
        Save-Checkpoint {{STEP_NUM}}
      } catch {
        Write-Color "Error: $_" -Color Red
        Invoke-FailurePrompt {{STEP_NUM}}
        Save-Checkpoint {{STEP_NUM}}
      }
    } else {
      Write-Color "⊘ Step {{STEP_NUM}} skipped" -Color Yellow
      Save-Checkpoint {{STEP_NUM}}
    }
  }
`;

/**
 * Escape a string for PowerShell single-quoted literal.
 * Only ' needs escaping (doubled).
 */
export function escapeForPowerShellSingleQuote(str: string): string {
  return str.replace(/'/g, "''");
}

/**
 * Encode arbitrary multi-line content as a PowerShell expression that
 * evaluates to a string array (safe to splat / -join).
 * Each line becomes a single-quoted string literal — the only escape
 * needed inside single-quoted PowerShell strings is '' for a literal '.
 */
export function encodeLinesAsPowerShellArray(content: string): string {
  if (content === '') {
    return "@('')";
  }
  const lines = content.split('\n');
  const quoted = lines.map(line => `'${escapeForPowerShellSingleQuote(line)}'`);
  return `@(${quoted.join(', ')})`;
}

/**
 * Default checkpoint filename for a PowerShell script target.
 * Just the filename — the script resolves it against the OS temp directory at runtime.
 */
export function defaultPowerShellCheckpointFilename(target: string): string {
  return `.leedoc-progress-${simpleHash(target)}`;
}

/**
 * Render a PowerShell expression that evaluates to the default checkpoint path
 * (resolves the OS temp dir at runtime — works on Windows, macOS, and Linux).
 */
function defaultCheckpointExpression(target: string): string {
  const name = defaultPowerShellCheckpointFilename(target);
  return `Join-Path ([System.IO.Path]::GetTempPath()) '${escapeForPowerShellSingleQuote(name)}'`;
}

/**
 * Quote a user-provided checkpoint file path as a single-quoted PowerShell literal.
 */
function literalCheckpointExpression(path: string): string {
  return `'${escapeForPowerShellSingleQuote(path)}'`;
}

function generateStepFunction(block: ContextualBlock, stepNum: number): string {
  const heading = block.context.heading || `Step ${stepNum}`;
  const description = block.context.description || '';
  const commandContent = block.content;

  return STEP_FUNCTION_TEMPLATE
    .replace(/\{\{STEP_NUM\}\}/g, String(stepNum))
    .replace(/\{\{HEADING_COMMENT\}\}/g, heading.replace(/[\r\n]+/g, ' '))
    .replace(/\{\{DESCRIPTION_LINES\}\}/g, encodeLinesAsPowerShellArray(description))
    .replace(/\{\{PREVIEW_LINES\}\}/g, encodeLinesAsPowerShellArray(commandContent))
    .replace(/\{\{COMMAND_BODY\}\}/g, commandContent);
}

function generateStepCall(block: ContextualBlock, stepNum: number): string {
  const heading = block.context.heading || `Step ${stepNum}`;

  return STEP_CALL_TEMPLATE
    .replace(/\{\{STEP_NUM\}\}/g, String(stepNum))
    .replace(/\{\{HEADING_ESCAPED\}\}/g, escapeForPowerShellSingleQuote(heading));
}

export class PowerShellScriptConverter extends ScriptConverter {
  readonly extensions = ['.ps1'];

  composeBlocks(blocks: string[]): string {
    return blocks.join('\n');
  }

  getOutputOptions(): ScriptOutputOption[] {
    return [
      { value: 'standard', label: 'Standard' },
      { value: 'interactive', label: 'Interactive' },
    ];
  }

  detectScriptLanguage(blocks: ContextualBlock[]): ScriptLanguage {
    if (blocks.length === 0) {
      return 'powershell';
    }

    const allPowerShell = blocks.every(block => isPowerShellLanguage(block.language));
    return allPowerShell ? 'powershell' : 'other';
  }

  supportsInteractiveBlocks(blocks: ContextualBlock[]): boolean {
    return this.detectScriptLanguage(blocks) !== 'other';
  }

  generateInteractiveScript(
    blocks: ContextualBlock[],
    options: InteractiveScriptOptions = {}
  ): InteractiveScriptResult {
    if (blocks.length === 0) {
      return {
        success: false,
        error: 'No blocks provided',
      };
    }

    if (!this.supportsInteractiveBlocks(blocks)) {
      return {
        success: false,
        error: 'Script contains non-powershell code blocks. Interactive mode only supports powershell/pwsh.',
      };
    }

    const {
      title = 'Installation Script',
      checkpointFile,
      allowResume = true,
    } = options;

    const checkpointExpression = checkpointFile
      ? literalCheckpointExpression(checkpointFile)
      : defaultCheckpointExpression(blocks[0].target);

    try {
      const stepFunctions = blocks
        .map((block, index) => generateStepFunction(block, index + 1))
        .join('\n');

      const stepCalls = blocks
        .map((block, index) => generateStepCall(block, index + 1))
        .join('\n\n');

      const script = INTERACTIVE_TEMPLATE
        .replace(/\{\{TITLE\}\}/g, title.replace(/[\r\n]+/g, ' '))
        .replace(/\{\{TITLE_ESCAPED\}\}/g, escapeForPowerShellSingleQuote(title))
        .replace(/\{\{TIMESTAMP\}\}/g, new Date().toISOString())
        .replace(/\{\{STEP_COUNT\}\}/g, String(blocks.length))
        .replace(/\{\{CHECKPOINT_FILE_EXPRESSION\}\}/g, checkpointExpression)
        .replace(/\{\{ALLOW_RESUME\}\}/g, allowResume ? 'true' : 'false')
        .replace(/\{\{STEP_FUNCTIONS\}\}/g, stepFunctions)
        .replace(/\{\{STEP_CALLS\}\}/g, stepCalls);

      return {
        success: true,
        script,
        stepCount: blocks.length,
      };
    } catch (err) {
      return {
        success: false,
        error: `Failed to generate interactive script: ${err}`,
      };
    }
  }
}
